"use client";

import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Typography } from '@/components/ui/Typography';
import { SongCard } from '@/components/shared/SongCard';
import { MovieCard } from '@/components/shared/MovieCard';
import { Button } from '@/components/ui/Button';
import { getClientSearchIndex } from '@/utils/clientIndex';
import { X, Sparkles, Heart, Flame, Frown, PartyPopper, SlidersHorizontal } from 'lucide-react';

function getLanguageVariants(lang) {
  if (!lang || lang === 'all') return null;
  const lower = lang.toLowerCase().trim();
  const titleCase = lower.charAt(0).toUpperCase() + lower.slice(1);
  const upper = lower.toUpperCase();
  return Array.from(new Set([lower, titleCase, upper]));
}

function matchesYearFilter(itemYear, filterYear) {
  if (!filterYear || filterYear === 'all') return true;
  if (!itemYear) return false;

  const yrNum = Number(itemYear);
  if (isNaN(yrNum)) return false;

  const strFilter = String(filterYear).trim().toLowerCase();

  // Decade filter, e.g. "2020s", "2010s", "2000s"
  if (strFilter.endsWith('s')) {
    const startDecade = parseInt(strFilter, 10);
    if (!isNaN(startDecade)) {
      return yrNum >= startDecade && yrNum <= startDecade + 9;
    }
  }

  // Single year filter, e.g. "2024"
  return yrNum === parseInt(strFilter, 10);
}

function getMovieYear(movie) {
  if (!movie) return null;
  if (movie.year) return Number(movie.year);
  if (movie.release_year) return Number(movie.release_year);
  if (movie.release_date) {
    const parsed = new Date(movie.release_date).getFullYear();
    if (!isNaN(parsed)) return parsed;
  }
  return null;
}

export function BrowseContent({ initialData }) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const urlCategory = searchParams.get('category') || 'songs';
  const urlLanguage = searchParams.get('language') || 'all';
  const urlYear = searchParams.get('year') || 'all';
  const urlMood = searchParams.get('mood') || 'all';

  // State for active filters
  const [category, setCategory] = useState(urlCategory);
  const [language, setLanguage] = useState(urlLanguage);
  const [year, setYear] = useState(urlYear);
  const [mood, setMood] = useState(urlMood);

  // State for data
  const [results, setResults] = useState(initialData || []);
  const [loading, setLoading] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const categories = [
    { id: 'songs', label: 'All Songs' },
    { id: 'movies', label: 'Movies' },
  ];

  const languages = [
    { id: 'all', label: 'All Languages' },
    { id: 'hindi', label: 'Hindi' },
    { id: 'tamil', label: 'Tamil' },
    { id: 'telugu', label: 'Telugu' },
    { id: 'malayalam', label: 'Malayalam' },
  ];

  const moods = [
    { id: 'all', label: 'All Moods', icon: Sparkles },
    { id: 'romantic', label: 'Romantic & Love', icon: Heart },
    { id: 'mass', label: 'Mass & High Energy', icon: Flame },
    { id: 'sad', label: 'Sad & Melancholy', icon: Frown },
    { id: 'dance', label: 'Dance & Party', icon: PartyPopper },
    { id: 'soulful', label: 'Soulful & Devotional', icon: Sparkles },
  ];

  // Keep state in sync with URL searchParams
  useEffect(() => {
    setCategory(urlCategory);
    setLanguage(urlLanguage);
    setYear(urlYear);
    setMood(urlMood);
  }, [urlCategory, urlLanguage, urlYear, urlMood]);

  // Update URL parameters when filters change
  const updateFilters = (newCategory, newLanguage, newYear = year, newMood = mood) => {
    setCategory(newCategory);
    setLanguage(newLanguage);
    setYear(newYear);
    setMood(newMood);

    const params = new URLSearchParams();
    if (newCategory) params.set('category', newCategory);
    if (newLanguage && newLanguage !== 'all') params.set('language', newLanguage);
    if (newYear && newYear !== 'all') params.set('year', newYear);
    if (newMood && newMood !== 'all') params.set('mood', newMood);

    router.push(`/browse?${params.toString()}`, { scroll: false });
  };

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        const langVariants = getLanguageVariants(language);
        const { songs: songsData, movies: moviesData } = await getClientSearchIndex();

        if (category === 'movies') {
          let filteredMovies = moviesData;
          if (langVariants) {
            const langSet = new Set(langVariants.map(l => l.toLowerCase()));
            filteredMovies = filteredMovies.filter(m => langSet.has((m.language || '').toLowerCase().trim()));
          }

          if (year && year !== 'all') {
            filteredMovies = filteredMovies.filter(m => matchesYearFilter(getMovieYear(m), year));
          }

          setResults(filteredMovies.slice(0, 150));
        } else {
          let filteredSongs = songsData;

          if (langVariants) {
            const langSet = new Set(langVariants.map(l => l.toLowerCase()));
            filteredSongs = filteredSongs.filter(s => langSet.has((s.language || '').toLowerCase().trim()));
          }

          // Filter by Mood if selected
          if (mood && mood !== 'all') {
            const targetMood = mood.toLowerCase().trim();
            filteredSongs = filteredSongs.filter(s => {
              const sMood = (s.mood || '').toLowerCase().trim();
              if (sMood === targetMood) return true;
              if (Array.isArray(s.tags) && s.tags.some(t => t.toLowerCase().includes(targetMood))) return true;
              return false;
            });
          }

          // Filter by Year if selected
          if (year && year !== 'all') {
            filteredSongs = filteredSongs.filter(s => matchesYearFilter(s.year, year));
          }

          setResults(filteredSongs.slice(0, 150));
        }

      } catch (error) {
        console.error("Error fetching browse data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [category, language, year, mood]);

  const activeMoodObj = moods.find(m => m.id === mood && m.id !== 'all');

  return (
    <>
      <div className="flex flex-col md:flex-row gap-8 items-start">

        {/* Sidebar Filters */}
        <div className="hidden md:block w-64 flex-shrink-0 space-y-8 bg-card border border-border p-6 rounded-2xl sticky top-24">

          {/* Category Filter */}
          <div>
            <h2 className="font-semibold mb-4 text-sm uppercase tracking-wider text-foreground/75 dark:text-foreground/70">Category</h2>
            <div className="flex flex-col gap-2">
              {categories.map(cat => (
                <Button
                  key={cat.id}
                  variant={category === cat.id ? 'primary' : 'ghost'}
                  className="justify-start"
                  onClick={() => updateFilters(cat.id, language, year, mood)}
                >
                  {cat.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Mood & Vibe Filter (Only shown when Category is Songs or All) */}
          {category === 'songs' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-sm uppercase tracking-wider text-foreground/75 dark:text-foreground/70">Mood & Vibe</h2>
                {mood !== 'all' && (
                  <button
                    onClick={() => updateFilters(category, language, year, 'all')}
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                {moods.map(m => {
                  const Icon = m.icon;
                  return (
                    <Button
                      key={m.id}
                      variant={mood === m.id ? 'primary' : 'ghost'}
                      size="sm"
                      className="justify-start text-xs gap-2"
                      onClick={() => updateFilters(category, language, year, m.id)}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {m.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Language Filter */}
          <div>
            <h2 className="font-semibold mb-4 text-sm uppercase tracking-wider text-foreground/75 dark:text-foreground/70">Language</h2>
            <div className="flex flex-col gap-2">
              {languages.map(lang => (
                <Button
                  key={lang.id}
                  variant={language === lang.id ? 'primary' : 'ghost'}
                  className="justify-start"
                  onClick={() => updateFilters(category, lang.id, year, mood)}
                >
                  {lang.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Release Year Filter */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-sm uppercase tracking-wider text-foreground/75 dark:text-foreground/70">Release Year</h2>
              {year !== 'all' && (
                <button
                  onClick={() => updateFilters(category, language, 'all', mood)}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Button
                variant={year === 'all' ? 'primary' : 'ghost'}
                size="sm"
                className="text-xs"
                onClick={() => updateFilters(category, language, 'all', mood)}
              >
                All
              </Button>
              {['2020s', '2010s', '2000s'].map(dec => (
                <Button
                  key={dec}
                  variant={year === dec ? 'primary' : 'outline'}
                  size="sm"
                  className="text-xs"
                  onClick={() => updateFilters(category, language, dec, mood)}
                >
                  {dec}
                </Button>
              ))}
              {[2026, 2025, 2024, 2023, 2022, 2021, 2020].map(yrVal => {
                const yrStr = String(yrVal);
                return (
                  <Button
                    key={yrStr}
                    variant={year === yrStr ? 'primary' : 'ghost'}
                    size="sm"
                    className="text-xs px-2.5 py-1"
                    onClick={() => updateFilters(category, language, yrStr, mood)}
                  >
                    {yrStr}
                  </Button>
                );
              })}
            </div>
          </div>

        </div>

        {/* Results Grid */}
        <div className="flex-grow w-full">
          {/* Mobile Categories & Filters */}
          <div className="flex md:hidden bg-secondary/20 p-1 rounded-xl mb-4 w-full border border-border/50">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`flex-1 text-center py-2 text-xs font-semibold rounded-lg transition-colors ${
                  category === cat.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-foreground/75 dark:text-foreground/70 hover:text-foreground'
                }`}
                onClick={() => updateFilters(cat.id, language, year, mood)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 mb-6 md:hidden">
            <Button
              variant="outline"
              className="flex-1 justify-center gap-2 font-medium border-border h-11 text-xs"
              onClick={() => setIsMobileFilterOpen(true)}
            >
              <SlidersHorizontal className="w-4 h-4 text-primary" />
              Filters {[language !== 'all', year !== 'all', mood !== 'all' && category === 'songs'].filter(Boolean).length > 0 ? `(${[language !== 'all', year !== 'all', mood !== 'all' && category === 'songs'].filter(Boolean).length})` : ''}
            </Button>
            
            <div className="flex-1">
              <select
                value={language}
                onChange={(e) => updateFilters(category, e.target.value, year, mood)}
                className="w-full text-xs font-medium bg-secondary/20 hover:bg-secondary/40 border border-border/80 rounded-xl px-3 py-3 outline-none focus:ring-1 focus:ring-primary/50 text-foreground"
              >
                <option value="all">All Languages</option>
                <option value="hindi">Hindi</option>
                <option value="tamil">Tamil</option>
                <option value="telugu">Telugu</option>
                <option value="malayalam">Malayalam</option>
              </select>
            </div>
          </div>

          {/* Active Filter Badges */}
          {(language !== 'all' || year !== 'all' || mood !== 'all') && (
            <div className="flex flex-wrap items-center gap-2 mb-6 p-3 rounded-xl bg-secondary/30 border border-border/60">
              <span className="text-xs font-semibold text-foreground/75 dark:text-foreground/70 mr-1">Active Filters:</span>
              
              {mood !== 'all' && activeMoodObj && (
                <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-pink-500/10 text-pink-500 font-semibold border border-pink-500/20">
                  Mood: {activeMoodObj.label}
                  <button onClick={() => updateFilters(category, language, year, 'all')} className="hover:text-foreground">
                    <X className="w-3 h-3 ml-1" />
                  </button>
                </span>
              )}

              {language !== 'all' && (
                <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-primary/10 text-primary font-semibold border border-primary/20">
                  Language: {language.toUpperCase()}
                  <button onClick={() => updateFilters(category, 'all', year, mood)} className="hover:text-foreground">
                    <X className="w-3 h-3 ml-1" />
                  </button>
                </span>
              )}

              {year !== 'all' && (
                <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-secondary text-foreground/80 font-semibold border border-border">
                  Year: {year}
                  <button onClick={() => updateFilters(category, language, 'all', mood)} className="hover:text-foreground">
                    <X className="w-3 h-3 ml-1" />
                  </button>
                </span>
              )}

              <button
                onClick={() => updateFilters('songs', 'all', 'all', 'all')}
                className="text-xs text-foreground/75 dark:text-foreground/70 hover:text-foreground underline ml-auto font-medium"
              >
                Reset All
              </button>
            </div>
          )}

          {loading ? (
            <div className={category === 'movies' ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-card/40 rounded-2xl animate-pulse border border-border/40" />
              ))}
            </div>
          ) : results.length > 0 ? (
            <div className={category === 'movies' ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"}>
              {category === 'movies'
                ? results.map(movie => <MovieCard key={movie.id} movie={movie} />)
                : results.map(song => <SongCard key={song.id} song={song} />)}
            </div>
          ) : (
            <div className="text-center py-16 bg-card border border-border rounded-2xl p-8">
              <Typography variant="h3" className="mb-2">No results found</Typography>
              <Typography variant="p" className="text-foreground/75 dark:text-foreground/70 mb-6">
                No items match your selected filters. Try clearing some filters.
              </Typography>
              <Button onClick={() => updateFilters('songs', 'all', 'all', 'all')}>
                Reset Filters
              </Button>
            </div>
          )}
        </div>

      </div>

      {/* Mobile Filter Drawer / Bottom Sheet */}
      {isMobileFilterOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex flex-col justify-end">
          {/* Dark Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setIsMobileFilterOpen(false)}
          />
          
          {/* Drawer Panel */}
          <div className="relative bg-card border-t border-border rounded-t-[2.5rem] shadow-2xl w-full max-h-[90vh] flex flex-col z-50 transform transition-transform duration-300 ease-out animate-in slide-in-from-bottom">
            {/* Notch handle indicator */}
            <div className="mx-auto my-3 w-12 h-1 bg-border rounded-full" />

            {/* Header */}
            <div className="flex items-center justify-between px-6 pb-4 border-b border-border/40">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-foreground">Filters</h2>
              </div>
              <button 
                onClick={() => setIsMobileFilterOpen(false)}
                className="p-1.5 rounded-full bg-secondary/50 hover:bg-secondary/80 text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 pb-24">
              {/* Category Filter */}
              <div>
                <h3 className="font-semibold text-xs uppercase tracking-wider text-foreground/60 mb-3">Category</h3>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map(cat => (
                    <Button
                      key={cat.id}
                      variant={category === cat.id ? 'primary' : 'outline'}
                      className="justify-center text-xs h-11 rounded-xl"
                      onClick={() => updateFilters(cat.id, language, year, mood)}
                    >
                      {cat.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Language Filter */}
              <div>
                <h3 className="font-semibold text-xs uppercase tracking-wider text-foreground/60 mb-3">Language</h3>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map(lang => (
                    <Button
                      key={lang.id}
                      variant={language === lang.id ? 'primary' : 'outline'}
                      className="justify-center text-xs h-11 rounded-xl"
                      onClick={() => updateFilters(category, lang.id, year, mood)}
                    >
                      {lang.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Mood & Vibe Filter */}
              {category === 'songs' && (
                <div>
                  <h3 className="font-semibold text-xs uppercase tracking-wider text-foreground/60 mb-3">Mood & Vibe</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {moods.map(m => {
                      const Icon = m.icon;
                      return (
                        <Button
                          key={m.id}
                          variant={mood === m.id ? 'primary' : 'outline'}
                          className="justify-start text-xs h-11 rounded-xl gap-2 px-3 truncate"
                          onClick={() => updateFilters(category, language, year, m.id)}
                        >
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{m.label}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Release Year Filter */}
              <div>
                <h3 className="font-semibold text-xs uppercase tracking-wider text-foreground/60 mb-3">Release Year</h3>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={year === 'all' ? 'primary' : 'outline'}
                    className="text-xs px-4 h-10 rounded-xl"
                    onClick={() => updateFilters(category, language, 'all', mood)}
                  >
                    All
                  </Button>
                  {['2020s', '2010s', '2000s'].map(dec => (
                    <Button
                      key={dec}
                      variant={year === dec ? 'primary' : 'outline'}
                      className="text-xs px-4 h-10 rounded-xl"
                      onClick={() => updateFilters(category, language, dec, mood)}
                    >
                      {dec}
                    </Button>
                  ))}
                  {[2026, 2025, 2024, 2023, 2022, 2021, 2020].map(yrVal => {
                    const yrStr = String(yrVal);
                    return (
                      <Button
                        key={yrStr}
                        variant={year === yrStr ? 'primary' : 'outline'}
                        className="text-xs px-4 h-10 rounded-xl"
                        onClick={() => updateFilters(category, language, yrStr, mood)}
                      >
                        {yrStr}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sticky Footer Actions */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-background/90 backdrop-blur-md border-t border-border/45 flex gap-3 z-50">
              {[language !== 'all', year !== 'all', mood !== 'all' && category === 'songs'].filter(Boolean).length > 0 && (
                <Button
                  variant="outline"
                  className="flex-1 h-12 rounded-xl text-xs font-semibold"
                  onClick={() => {
                    updateFilters('songs', 'all', 'all', 'all');
                    setIsMobileFilterOpen(false);
                  }}
                >
                  Reset All
                </Button>
              )}
              <Button
                variant="primary"
                className="flex-grow h-12 rounded-xl text-xs font-bold shadow-lg shadow-primary/20"
                onClick={() => setIsMobileFilterOpen(false)}
              >
                Show {results.length} Results
              </Button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
